import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import { ChevronLeft, ChevronRight, ChevronsUpDown, LogOut } from "lucide-react";
import { cn } from "@/shared/utils.js";
import { useAuth } from "../../features/auth/AuthProvider.js";
import { useSidebarCollapsed } from "./useSidebarCollapsed.js";
import { primaryNavItems, settingsNavItems } from "./navItems.js";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/tooltip.js";

// The itemPath === "/" branch is unreachable today (Library now lives at
// /recipes) but is kept exact-match on purpose: Plan 12 Task 2 attaches a
// "today" nav item at "/" and it must NOT light up for every route (which
// startsWith("/") would do).
function isActive(pathname: string, itemPath: string): boolean {
  if (itemPath === "/") return pathname === "/";
  return pathname === itemPath || pathname.startsWith(itemPath + "/");
}

export function AppSidebar(): JSX.Element {
  const auth = useAuth();
  const { me } = auth;
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed, toggle } = useSidebarCollapsed();

  const currentHouseholdId = me?.user.currentHouseholdId ?? null;
  const householdName =
    me?.memberships.find((m) => m.householdId === currentHouseholdId)
      ?.householdName ?? "";
  const householdInitial = householdName[0]?.toUpperCase() ?? "?";

  const displayName = me?.user.displayName ?? "";
  const email = me?.user.email ?? "";
  const initials = displayName[0]?.toUpperCase() ?? "?";

  return (
    <TooltipProvider>
      <aside
        className={cn(
          "border-r border-zinc-800 bg-zinc-900 flex flex-col transition-all duration-200",
          collapsed ? "w-14" : "w-60",
        )}
        aria-label="Sidebar navigation"
      >
        {/* Header: logo + toggle */}
        <div
          className={cn(
            "px-4 py-4 border-b border-zinc-800 flex items-center",
            collapsed ? "flex-col gap-2" : "justify-between",
          )}
        >
          {!collapsed && (
            <img
              src="/wordmark.svg"
              alt="RecipeVault"
              className="h-7 w-auto"
            />
          )}
          {collapsed && (
            <img
              src="/favicon.svg"
              alt="RecipeVault"
              className="h-7 w-7"
            />
          )}
          <button
            onClick={toggle}
            className="w-7 h-7 rounded-md hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-200"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronLeft className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>

        {/* Household chip */}
        <div className={cn("py-3 border-b border-zinc-800", collapsed ? "flex justify-center" : "px-2")}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="w-9 h-9 rounded-md bg-emerald-700 hover:ring-2 hover:ring-emerald-500/40 flex items-center justify-center text-xs font-medium text-emerald-50"
                  aria-label={householdName}
                >
                  {householdInitial}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{householdName}</TooltipContent>
            </Tooltip>
          ) : (
            <button className="w-full text-left rounded-md px-2 py-1.5 hover:bg-zinc-800 flex items-center gap-2 text-sm">
              <span className="w-6 h-6 rounded-md bg-emerald-700 flex items-center justify-center text-xs font-medium text-emerald-50">
                {householdInitial}
              </span>
              <span className="text-zinc-50 flex-1 truncate">{householdName}</span>
              <ChevronsUpDown className="h-4 w-4 text-zinc-400 shrink-0" aria-hidden />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className={cn("flex-1 py-3 space-y-0.5", collapsed ? "px-2" : "px-2")} aria-label="Main navigation">
          {primaryNavItems.map((item) => {
            const active = isActive(location.pathname, item.path);
            const Icon = item.icon;
            return collapsed ? (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { void navigate(item.path); }}
                    className={cn(
                      "relative w-full flex items-center justify-center rounded-md py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                      active
                        ? "text-emerald-400 bg-zinc-800"
                        : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                    )}
                    aria-current={active ? "page" : undefined}
                    aria-label={item.label}
                  >
                    {active && (
                      <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                    )}
                    <Icon className="h-5 w-5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              <button
                key={item.id}
                onClick={() => { void navigate(item.path); }}
                className={cn(
                  "relative w-full flex items-center gap-3 rounded-md px-2 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                  active
                    ? "text-emerald-400 bg-zinc-800"
                    : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                )}
                aria-current={active ? "page" : undefined}
              >
                {active && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                )}
                <Icon className="h-5 w-5" aria-hidden />
                <span>{item.label}</span>
              </button>
            );
          })}

          {/* Settings group */}
          {collapsed ? (
            <div className="my-3 mx-0 h-px bg-zinc-800" />
          ) : (
            <div className="pt-4 pb-1 px-2 text-[0.6875rem] font-medium uppercase tracking-wider text-zinc-500">
              Settings
            </div>
          )}

          {settingsNavItems.map((item) => {
            const active = isActive(location.pathname, item.path);
            const Icon = item.icon;
            return collapsed ? (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { void navigate(item.path); }}
                    className={cn(
                      "relative w-full flex items-center justify-center rounded-md py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                      active
                        ? "text-emerald-400 bg-zinc-800"
                        : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                    )}
                    aria-current={active ? "page" : undefined}
                    aria-label={item.label}
                  >
                    {active && (
                      <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                    )}
                    <Icon className="h-5 w-5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              <button
                key={item.id}
                onClick={() => { void navigate(item.path); }}
                className={cn(
                  "relative w-full flex items-center gap-3 rounded-md px-2 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                  active
                    ? "text-emerald-400 bg-zinc-800"
                    : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                )}
                aria-current={active ? "page" : undefined}
              >
                {active && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                )}
                <Icon className="h-5 w-5" aria-hidden />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Account anchor */}
        <div className="border-t border-zinc-800 p-2">
          {collapsed ? (
            <div className="flex justify-center py-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="w-9 h-9 rounded-full bg-zinc-700 text-zinc-100 text-xs font-medium flex items-center justify-center hover:ring-2 hover:ring-emerald-500/40"
                    aria-label={`${displayName} — account`}
                  >
                    {initials}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{displayName}</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-2 py-1.5">
              <span className="w-8 h-8 rounded-full bg-zinc-700 text-zinc-100 text-xs font-medium flex items-center justify-center shrink-0">
                {initials}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-100 truncate">{displayName}</div>
                <div className="text-[0.6875rem] text-zinc-500 truncate">{email}</div>
              </div>
              <button
                onClick={() => { void auth.signOut(); }}
                className="text-zinc-400 hover:text-zinc-200 w-7 h-7 rounded-md hover:bg-zinc-800 flex items-center justify-center shrink-0"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
